import { commandRegistry, CommandError } from './registry';
import type { CommandContext } from './registry';
import { channelDisplayName } from '../lib/channelDisplay';
import { useMessageStore } from '../stores/useMessageStore';
import { useChannelStore } from '../stores/useChannelStore';
import { useDmStore } from '../stores/useDmStore';
import { onChannelLeave } from '../stores/actions';

commandRegistry.register({
  name: 'help',
  description: '显示所有可用命令',
  usage: '/help',
  paramType: 'none',
  execute: async ({ channelId }: CommandContext) => {
    const groups = commandRegistry.search('');
    const sections = groups.map(g => {
      const isSystem = g.group === 'System';
      const header = isSystem ? `── ${g.group} ──` : `── 🤖 ${g.group} ──`;
      const lines = g.items.map(c => `\`${c.usage}\` — ${c.description}`);
      return [header, ...lines].join('\n');
    });
    useMessageStore.getState().insertLocalSystemMessage(channelId, sections.join('\n\n'));
  },
});

commandRegistry.register({
  name: 'leave',
  description: '离开当前频道',
  usage: '/leave',
  paramType: 'none',
  execute: async ({ channelId, api }: CommandContext) => {
    const confirmed = window.confirm('确定离开当前频道？');
    if (!confirmed) return;
    await api.leaveChannel(channelId);
    onChannelLeave(channelId);
  },
});

commandRegistry.register({
  name: 'topic',
  description: '设置频道主题',
  usage: '/topic <text>',
  paramType: 'text',
  placeholder: '输入频道主题…',
  execute: async ({ channelId, args, api }: CommandContext) => {
    if (!args.trim()) throw new CommandError('Usage: /topic <text>');
    await api.updateChannel(channelId, { topic: args.trim() });
  },
});

commandRegistry.register({
  name: 'invite',
  description: '邀请用户加入频道',
  usage: '/invite @user',
  paramType: 'user',
  placeholder: '选择用户…',
  execute: async ({ channelId, resolvedUser, api }: CommandContext) => {
    if (!resolvedUser) throw new CommandError('Usage: /invite @user');
    await api.addChannelMember(channelId, resolvedUser.id);
  },
});

commandRegistry.register({
  name: 'dm',
  description: '打开与用户的私信',
  usage: '/dm @user',
  paramType: 'user',
  placeholder: '选择用户…',
  execute: async ({ resolvedUser }: CommandContext) => {
    if (!resolvedUser) throw new CommandError('Usage: /dm @user');
    await useDmStore.getState().openDm(resolvedUser.id);
  },
});

commandRegistry.register({
  name: 'status',
  description: '显示频道状态',
  usage: '/status',
  paramType: 'none',
  execute: async ({ channelId, api }: CommandContext) => {
    const { channel } = await api.getChannel(channelId);
    const members = await api.fetchChannelMembers(channelId);
    const onlineIds = await api.fetchOnlineUsers();
    const onlineSet = new Set(onlineIds);
    const online = members.filter(m => onlineSet.has(m.user_id));
    const offline = members.filter(m => !onlineSet.has(m.user_id));

    const lines = [
      `**#${channelDisplayName(channel)}**`,
      `主题: ${channel.topic || '无'}`,
      `成员: ${members.length} (在线 ${online.length})`,
      '',
      online.length ? `🟢 在线: ${online.map(m => m.display_name).join(', ')}` : '',
      offline.length ? `⚫ 离线: ${offline.map(m => m.display_name).join(', ')}` : '',
    ].filter(Boolean);

    useMessageStore.getState().insertLocalSystemMessage(channelId, lines.join('\n'));
  },
});

commandRegistry.register({
  name: 'clear',
  description: '清除本地聊天记录',
  usage: '/clear',
  paramType: 'none',
  execute: async ({ channelId }: CommandContext) => {
    const confirmed = window.confirm('确定清除本地聊天记录？仅清除本地显示，不影响服务端数据。');
    if (!confirmed) return;
    useMessageStore.getState().clearLocalMessages(channelId);
    useMessageStore.getState().insertLocalSystemMessage(channelId, '🗑️ 本地聊天记录已清除');
  },
});

commandRegistry.register({
  name: 'nick',
  description: '修改显示名',
  usage: '/nick <name>',
  paramType: 'text',
  placeholder: '新显示名…',
  execute: async ({ channelId, args, api }: CommandContext) => {
    if (!args.trim()) throw new CommandError('Usage: /nick <name>');
    const oldUser = await api.fetchMe();
    const oldName = oldUser.display_name;
    await api.updateProfile({ display_name: args.trim() });
    useMessageStore.getState().insertLocalSystemMessage(channelId, `✅ 昵称已修改: ${oldName} → ${args.trim()}`);
  },
});
